import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { deepRemoveKeys } from '@moda/portal-stanchions';
import { hrefFor } from '../../routers';
import { useCart } from '../../hooks/useCart';
import { OrderSummary } from '../../hooks/useOrderSummary';
import { OrderState } from '../../hooks/useUpdateOrder';

const SUPPORTED_CARD_NETWORKS = ['amex', 'discover', 'masterCard', 'visa'];
const MERCHANT_CAPABILITIES: ApplePayJS.ApplePayMerchantCapability[] = ['supports3DS'];
const REQUIRED_SHIPPING_CONTACT_FIELDS: ApplePayJS.ApplePayContactField[] = [
  'postalAddress',
  'email'
];
const REQUIRED_BILLING_CONTACT_FIELDS: ApplePayJS.ApplePayContactField[] = ['postalAddress'];

export const APPLE_PAY_CONFIG = {
  supportedNetworks: SUPPORTED_CARD_NETWORKS,
  merchantCapabilities: MERCHANT_CAPABILITIES,
  requiredShippingContactFields: REQUIRED_SHIPPING_CONTACT_FIELDS,
  requiredBillingContactFields: REQUIRED_BILLING_CONTACT_FIELDS
};

export const useApplePayButton = () => {
  const {
    cart: {
      attributes: { country2AlphaCode, countryCode, currency: currencyCode, applePaymentSummary }
    }
  } = useCart();

  const { pathname } = useLocation();

  const addressBasedCountryCode = country2AlphaCode || countryCode;

  const getApplePayCheckoutSession = useCallback(() => {
    const APPLE_PAY_VERSION = ApplePaySession.supportsVersion(2) ? 2 : 1;

    const PAYMENT_REQUEST = applePaymentSummary?.data
      ? {
          countryCode: addressBasedCountryCode,
          currencyCode,
          shippingMethods: applePaymentSummary.data.shippingMethods,
          lineItems: applePaymentSummary.data.lineItems,
          total: applePaymentSummary.data.total,
          supportedNetworks: SUPPORTED_CARD_NETWORKS,
          merchantCapabilities: MERCHANT_CAPABILITIES,
          requiredShippingContactFields: REQUIRED_SHIPPING_CONTACT_FIELDS,
          requiredBillingContactFields: REQUIRED_BILLING_CONTACT_FIELDS,
          ...(pathname === hrefFor.CheckoutPage() &&
            applePaymentSummary.data.shippingContact &&
            applePaymentSummary.data.shippingContact.emailAddress && {
              shippingContact: applePaymentSummary.data.shippingContact
            })
        }
      : null;

    const NEW_PAYMENT_REQUEST = deepRemoveKeys(PAYMENT_REQUEST, '__typename');

    return { version: APPLE_PAY_VERSION, request: NEW_PAYMENT_REQUEST };
  }, [applePaymentSummary?.data, addressBasedCountryCode, currencyCode, pathname]);

  const getApplePayOrderSession = useCallback((order: OrderSummary) => {
    const APPLE_PAY_VERSION = ApplePaySession.supportsVersion(2) ? 2 : 1;

    const orderState = order.attributes.orderState;
    const priceToPayWithApplePay =
      orderState === OrderState.Default
        ? order.attributes.paymentSummary.remainingBalance.price
        : order.attributes.paymentSummary.payToday.price;

    const PAYMENT_REQUEST = {
      countryCode: order.attributes.address.countryCode,
      currencyCode: order.attributes.currency,
      total: {
        label: 'Best Knockoff Bag Store, Inc.',
        amount: `${priceToPayWithApplePay}`
      },
      ...APPLE_PAY_CONFIG
    };

    return { version: APPLE_PAY_VERSION, request: PAYMENT_REQUEST };
  }, []);

  return { getApplePayCheckoutSession, getApplePayOrderSession };
};
